import { ArrayUtil, Color, Keys, LogManager } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Component } from "../../base/Component";
import { ComponentTypes } from "../../base/ComponentTypes";
import { SelectionMode, SelectionType } from "../../base/SelectionMode";
import { ChangeEvent, ChangeListener } from "../../events/ChangeEvent";
import { ClickEvent } from "../../events/ClickEvent";
import { DomEvent } from "../../events/DomEvent";
import { KeyEvent } from "../../events/KeyEvent";
import { KeyModifiers } from "../../keyhandling/KeyModifiers";
import { TreeNode, TreeNodeSortMethod } from "./TreeNode";
import { TreePropDefinitions, TreeProps } from "./TreeProps";
import { TreeStyles } from "./TreeStyles";

const log = LogManager.getLogger("components.tree.Tree");
const treeHandledKeys = [Keys.ARROW_DOWN, Keys.ARROW_UP, Keys.ARROW_LEFT, Keys.ARROW_RIGHT];
const _changeListenerDef = { listName: "_changeListeners", domEventName: "input", eventCreatorFunction: (component, event) => new ChangeEvent(component, component.text, component.text, event) };

export class Tree<NodeType extends TreeNode = TreeNode> extends Component implements TreeProps {
    private rootNode: TreeNode;
    private _selectedNodes: NodeType[];
    private _selOriginalBackground: Color;
    private _selOriginalColor: Color;
    private _selectedNodeBackgroundColor: Color| ((selectedNode:TreeNode) => Color);
    private _selectedNodeColor: Color | ((selectedNode:TreeNode) => Color);
    public adHocKeyHandler: (event: KeyEvent) => void;
    public nodeLeafImageName: ImageName;
    public nodeDefaultImageName: ImageName;
    public nodeExpandedImageName: ImageName;
    public nodeCollapsedImageName: ImageName;
    private _selectionMode: SelectionMode;

    constructor(props?: Partial<TreeProps>) {
        super("ul", props);
        this._selectedNodes = [];
        this._element.tabIndex = 0;
        this._element.className += " " + TreeStyles.treeBase;
        if (props == null)
            props = {};
        if (props.borderColor === undefined)
            props.borderColor = "strokeSecondary";
        if (props.borderWidth === undefined)
            props.borderWidth = 1;
        this.rootNode = new TreeNode({ id: "rootNode", parent: this, tree: this });
        this.addKeyDownListener((event: KeyEvent) => this.handleKey(event));
        this._element.appendChild(this.rootNode._element);
        this.setProps(props);
    }

    setProps(props: Partial<TreeProps>) {
        super.setProps(props);
    }

    public get selectionMode(): SelectionMode {
        return this._selectionMode || this.getPropertyDefinitions().selectionMode.defaultValue;
    }

    public set selectionMode(value: SelectionMode) {
        this._selectionMode = value;
    }

    public get selectedNodeBackgroundColor(): Color | ((selectedNode:TreeNode) => Color) {
        return this._selectedNodeBackgroundColor || this.getPropertyDefinitions().selectedNodeBackgroundColor.defaultValue;
    }

    public set selectedNodeBackgroundColor(value: Color| ((selectedNode:TreeNode) => Color)) {
        this._selectedNodeBackgroundColor = value;
    }

    public get selectedNodeColor(): Color | ((selectedNode:TreeNode) => Color) {
        return this._selectedNodeColor || this.getPropertyDefinitions().selectedNodeColor.defaultValue;
    }

    public set selectedNodeColor(value: Color | ((selectedNode:TreeNode) => Color)) {
        this._selectedNodeColor = value;
    }

    private resolveColor(node: TreeNode, color: Color | ((selectedNode:TreeNode) => Color)): Color {
        if (typeof color === "function")
            return color(node);
        return color;
    }

    get selectedNode(): NodeType {
        return ArrayUtil.getFirstElement(this._selectedNodes);
    }

    set selectedNode(node: NodeType) {
        this.setSelectedNodeInternal(node, null);
    }

    selectNode(node: NodeType, selectionType: SelectionType, event: DomEvent) {
        if (this.selectionMode == null || this.selectionMode === SelectionMode.NONE)
            return;
        if (window.getSelection().type === "Caret")
            window.getSelection().removeAllRanges(); // this keeps shift-clicking rows from selecting all the HTMLElements between the two points the user clicked
        if (node == null) {
            this.setSelectedNodesInternal([], event);
            return;
        }
        if (this.selectionMode === SelectionMode.SINGLE)
            selectionType = SelectionType.SINGLE;
        if (selectionType === SelectionType.SINGLE)
            this.handleSingleSelection(node, event);
        else if (selectionType === SelectionType.DISTINCT)
            this.handleDistinctSelection(node, event);
        else if (selectionType === SelectionType.RANGE)
            this.handleRangeSelection(node, event);
    }


    private handleSingleSelection(node: NodeType, event: DomEvent | ClickEvent) {
        const sel = this.selectedNodes;
        const isSelected = sel?.length === 1 && sel[0] === node;
        const shouldDeselect = isSelected && (!(event instanceof ClickEvent) || event.hasModifiers({ ctrlKey: true }));

        this.setSelectedNodesInternal(shouldDeselect ? [] : [node], event as DomEvent);
    }

    private handleDistinctSelection(node: NodeType, event: DomEvent) {
        const newSel = [...this.selectedNodes];
        const index = newSel.indexOf(node);
        if (index < 0) {
            newSel.push(node);
        }
        else
            newSel.splice(index, 1);
        this.setSelectedNodesInternal([...newSel], event);
    }

    private handleRangeSelection(node: NodeType, event: DomEvent) {
        const sel = this.selectedNodes;
        const childNodes = this.getRootNode().getRecursiveChildNodes(child => child.isLeaf());

        let newSel;
        if (sel.length === 0)
            newSel = [node];
        else {
            const thisSelIndex = childNodes.indexOf(node);
            const firstSelIndex = childNodes.indexOf(sel[0]);
            const lastSelIndex = childNodes.indexOf(sel[sel.length - 1]);
            const minSelIndex = Math.min(thisSelIndex, firstSelIndex);
            const maxSelIndex = Math.max(thisSelIndex, lastSelIndex);
            newSel = [];
            for (let index = minSelIndex; index <= maxSelIndex; index++)
                newSel.push(childNodes[index]);
        }
        this.setSelectedNodesInternal(newSel, event);
    }

    private setSelectedNodeInternal(nodes: NodeType, domEvent: DomEvent) {
        this.setSelectedNodesInternal([nodes], domEvent);
    }

    private setSelectedNodesInternal(nodes: NodeType[], domEvent: DomEvent) {
        if (this.selectionMode === SelectionMode.NONE)
            return;

        const oldValue = this._selectedNodes;
        const newValue = (nodes ?? []).filter(node => node != null && node.selectable !== false);

        if (ArrayUtil.equals(this._selectedNodes, newValue))
            return;

        const unselectedNodes = this._selectedNodes.filter(node => !newValue.includes(node));
        const addedNodes = newValue.filter(node => !this._selectedNodes.includes(node));

        for (const node of unselectedNodes) {
            node._component.backgroundColor = this._selOriginalBackground;
            node._component.color = this._selOriginalColor;
        }

        this._selectedNodes = newValue;

        for (const node of addedNodes) {
            this._selOriginalBackground ??= node._component.backgroundColor;
            this._selOriginalColor ??= node._component.color;
            node._component.backgroundColor = this.resolveColor(node, this.selectedNodeBackgroundColor);
            node._component.color = this.resolveColor(node, this.selectedNodeColor);
        }

        const event = new ChangeEvent(this, oldValue, this._selectedNodes, domEvent);
        this.fireListeners(_changeListenerDef, event);
    }
    get selectedNodes(): NodeType[] {
        return this._selectedNodes;
    }

    set selectedNodes(value: NodeType[]) {
        this.setSelectedNodesInternal(value, null);
    }

    /**
     *
     * @param {KeyEvent} event
     */
    handleKey(event: KeyEvent) {
        if (this.adHocKeyHandler != null) {
            this.adHocKeyHandler(event);
            if (event.defaultPrevented)
                return;
        }
        if (event.altKey || event.ctrlKey)
            return;
        if (treeHandledKeys.includes(event.key)) {
            const root = this.getRootNode();
            const sel = this.selectedNode;
            if (root.getChildCount() > 0) {
                if (sel == null)
                    this.setSelectedNodeInternal(root.getChild(0) as NodeType, event.domEvent);
                else {
                    const parent = sel.parent as NodeType;
                    const parentIndex = sel.parent.indexOfChild(sel);
                    const noModifiers: KeyModifiers = { ctrlKey: false, shiftKey: false, altKey: false };
                    if (event.isKey(Keys.ARROW_DOWN, noModifiers) === true)
                        this.handleArrowDown(sel, parent, parentIndex, event.domEvent);
                    else if (event.isKey(Keys.ARROW_UP, noModifiers) === true)
                        this.handleArrowUp(sel, parent, parentIndex, event.domEvent);
                    else if (event.isKey(Keys.ARROW_RIGHT, noModifiers) === true)
                        this.setNodeExpanded(sel, true, event.domEvent);
                    else if (event.isKey(Keys.ARROW_LEFT, noModifiers) === true)
                        this.setNodeExpanded(sel, false, event.domEvent);
                }
            }
            event.preventDefault();
            event.stopPropagation();
        }
    }

    handleArrowDown(sel: NodeType, parent: NodeType, parentIndex: number, event: KeyboardEvent) {
        if (sel.expanded && sel.getChildCount() > 0)
            this.setSelectedNodeInternal(sel.getChild(0) as NodeType, event);
        else if (parentIndex < parent.getChildCount() - 1)
            this.setSelectedNodeInternal(parent.getChild(parentIndex + 1) as NodeType, event);
        else if (!(parent.isRoot()) && parent.parentIndex < parent.parent.getChildCount() - 1)
            this.setSelectedNodeInternal(parent.parent.getChild(parent.parentIndex + 1) as NodeType, event);
        this.scrollToSelection();
    }

    handleArrowUp(sel: NodeType, parent: NodeType, parentIndex: number, event: KeyboardEvent) {
        if (parentIndex === 0) {
            if (!parent.isRoot())
                this.setSelectedNodeInternal(parent, event);
        }
        else {
            const nextSib = parent.getChild(parentIndex - 1) as NodeType;
            if (nextSib.expanded && nextSib.getChildCount() > 0)
                this.setSelectedNodeInternal(nextSib.getChild(nextSib.getChildCount() - 1) as NodeType, event);
            else
                this.setSelectedNodeInternal(nextSib, event);
        }
        this.scrollToSelection();
    }

    scrollToSelection() {
        if (this.selectedNode != null)
            this.selectedNode.scrollIntoView();
    }

    /**
     *
     * @param {NodeType} node
     * @param {bool} value
     * @returns
     */
    setNodeExpanded(node: NodeType, value: boolean, event: KeyboardEvent) {
        if (value && node.getChildCount() > 0)
            node.expanded = true;
        else if (!value) {
            while (node.isRoot() !== true) {
                if (node.expanded && node.getChildCount() > 0) {
                    node.expanded = false;
                    this.setSelectedNodeInternal(node, event);
                    break;
                }
                node = node.parent as NodeType;
            }
        }
    }

    /**
     *
     * @returns {NodeType}
     */
    getRootNode(): TreeNode {
        return this.rootNode;
    }

    /**
     *
     * @param {bool} isLeaf
     * @param {bool} isExpanded
     * @returns {string}
     */
    getDefaultNodeImageName(isLeaf: boolean, isExpanded: boolean): ImageName {
        if (isLeaf)
            return this.nodeLeafImageName || this.nodeDefaultImageName;
        else if (isExpanded)
            return this.nodeExpandedImageName || this.nodeDefaultImageName || ImageName.checkboxIndeterminate;
        else
            return this.nodeCollapsedImageName || this.nodeDefaultImageName || ImageName.addInBox;
    }

    /**
     *
     * @param {*} object
     * @param {string} textField
     * @param {string} childField
     * @returns
     */
    makeTreeNodesFromObject(object: any, textField: string, childField: string, nodeCallback?: (node, data) => void): TreeNode {
        if (object == null)
            return null;
        const text = object[textField];
        const children = object[childField];
        if (text != null || (children != null && children.length > 0)) {
            const result = new TreeNode({ tree: this });
            if (text != null)
                result.text = text;
            result.data = object;
            if (nodeCallback != null)
                nodeCallback(result, object);
            for (let i = 0; children != null && i < children.length; i++) {
                const child = this.makeTreeNodesFromObject(children[i], textField, childField, nodeCallback);
                if (child != null)
                    result.addChild(child);
            }
            return result;
        }
        return null;
    }

    /**
     * We can definitely do a better job with performance of this.  Going with brute force for now.
     * This really just doesn't work well.  Maybe remove it?
     *
     * @param {string} filterValue
     */
    filter(filterValue: string, fillFunction: (filterValue: string) => NodeType) {
        this.getRootNode().removeAllChildren();
        const root = fillFunction(filterValue);
        this.filterNode(filterValue, root);
        this.getRootNode().setChildren(root.getChildren());
    }

    /**
      *
      * @param {string} filterValue
      * @param {NodeType} node
    */
    filterNode(filterValue: string, node: NodeType) {
        filterValue = filterValue.toLowerCase();
        for (let i = node.getChildCount() - 1; i >= 0; i--)
            if (node.getChildCount() === 0 && node.getChild(i).text.toLowerCase().indexOf(filterValue) < 0)
                node.removeChild(i);
        if (node.getChildCount() === 0 && node.parent != null)
            node.parent.removeChild(node.parentIndex);
    }

    public addChangeListener(value: ChangeListener) {
        this.addEventListener(_changeListenerDef, value);
    }

    public findNodeWithText(text: string): NodeType {
        return this.findChildWithText(text, this.getRootNode() as NodeType);
    }

    public findChildWithText(text: string, node: NodeType): NodeType {
        const textLowerCase = text.toLowerCase();
        for (let x=0 ; x<node.getChildCount() ; x++) {
            const childNode = node.children[x];
            if (childNode.text?.toLowerCase() === textLowerCase) {
                return childNode as NodeType;
            }
            const childChild = this.findChildWithText(text, childNode as NodeType);
            if (childChild != null) {
                return childChild;
            }
        }
        return null;
    }

    public removeChangeListener(value: ChangeListener) {
        this.removeEventListener(_changeListenerDef, value);
    }

    override getPropertyDefinitions() {
        return TreePropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "tree";
    }

    override get properName(): string {
        return "Tree";
    }

    public sort(sortMethod?: TreeNodeSortMethod) {
        this.rootNode?.children?.forEach(childNode => childNode.sort(sortMethod));
    }
}

ComponentTypes.registerComponentType("tree", {
    constructor: Tree.prototype.constructor
});
